import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';

const gcd = (a,b) => { a = Math.abs(a); b = Math.abs(b); while(b) { [a,b] = [b, a%b]; } return a || 1; };
const simplify = (num, den) => { const g = gcd(num, den); return [num/g, den/g]; };

/* ---------------- Fraction — Simplify ---------------- */
export function FractionSimplifyCalc() {
  const [num, setNum] = useState(24);
  const [den, setDen] = useState(36);
  const [n2, d2] = simplify(+num||0, +den||1);
  return (
    <CalcShell look="cnet" title="Simplify Fractions Calculator" subtitle="Reduce a fraction to its lowest terms.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Numerator</label><input type="number" value={num} onChange={e=>setNum(+e.target.value||0)} data-testid="fs-num"/></div>
          <div className="field"><label>Denominator</label><input type="number" value={den} onChange={e=>setDen(+e.target.value||1)} data-testid="fs-den"/></div>
        </div>
        <div>
          <div className="result-panel"><div className="result-title">Simplified</div><div className="result-value" data-testid="fs-result">{num}/{den} = <span className="text-blue-700">{n2}/{d2}</span></div></div>
        </div>
      </div>
      <StepsPanel steps={[`GCD(${num}, ${den}) = ${gcd(+num||0, +den||1)}`, `${num}/${den} ÷ ${gcd(+num||0, +den||1)}/${gcd(+num||0, +den||1)} = <b>${n2}/${d2}</b>`]}/>
    </CalcShell>
  );
}

/* ---------------- Fraction — Decimal → Fraction ---------------- */
export function DecimalToFractionCalc() {
  const [dec, setDec] = useState(0.75);
  const c = useMemo(() => {
    const d = +dec || 0;
    if (Number.isInteger(d)) return { num: d, den: 1 };
    const s = String(d);
    const places = (s.split('.')[1] || '').length;
    const den = Math.pow(10, places);
    const num = Math.round(d * den);
    return simplify(num, den);
  }, [dec]);
  return (
    <CalcShell title="Decimal to Fraction Calculator" subtitle="Convert a decimal number to its simplest fraction form.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div><div className="field"><label>Decimal</label><input type="number" step="any" value={dec} onChange={e=>setDec(e.target.value)} data-testid="d2f-in"/></div></div>
        <div><div className="result-panel"><div className="result-title">Fraction</div><div className="result-value" data-testid="d2f-out">{c[0]}/{c[1]}</div></div></div>
      </div>
    </CalcShell>
  );
}

/* ---------------- Fraction — Fraction → Decimal ---------------- */
export function FractionToDecimalCalc() {
  const [num, setNum] = useState(3);
  const [den, setDen] = useState(8);
  const value = (den === 0) ? 0 : (+num || 0) / (+den || 1);
  return (
    <CalcShell title="Fraction to Decimal Calculator" subtitle="Convert a fraction to its decimal equivalent.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Numerator</label><input type="number" value={num} onChange={e=>setNum(+e.target.value||0)} data-testid="f2d-num"/></div>
          <div className="field"><label>Denominator</label><input type="number" value={den} onChange={e=>setDen(+e.target.value||1)} data-testid="f2d-den"/></div>
        </div>
        <div><div className="result-panel"><div className="result-title">Decimal</div><div className="result-value" data-testid="f2d-out">{value.toFixed(10).replace(/\.?0+$/,'')}</div></div></div>
      </div>
    </CalcShell>
  );
}

/* ---------------- Fraction — Mixed Numbers ---------------- */
export function MixedNumberCalc() {
  const [w1, setW1] = useState(1); const [n1, setN1] = useState(1); const [d1, setD1] = useState(2);
  const [op, setOp] = useState('+');
  const [w2, setW2] = useState(2); const [n2, setN2] = useState(1); const [d2, setD2] = useState(3);
  const [showExpl, setShowExpl] = useState(false);
  const A = (+w1||0)*(+d1||1) + (+n1||0);
  const B = (+w2||0)*(+d2||1) + (+n2||0);
  let num = 0, den = 1;
  if (op === '+') { num = A*(+d2||1) + B*(+d1||1); den = (+d1||1)*(+d2||1); }
  else if (op === '-') { num = A*(+d2||1) - B*(+d1||1); den = (+d1||1)*(+d2||1); }
  else if (op === '*') { num = A*B; den = (+d1||1)*(+d2||1); }
  else if (op === '/') { num = A*(+d2||1); den = (+d1||1)*B; }
  const [sn, sd] = simplify(Math.abs(num), Math.abs(den));
  const sign = (num/den) < 0 ? '-' : '';
  const whole = Math.floor(sn / sd);
  const rem = sn - whole * sd;
  const lcm = ((+d1||1) * (+d2||1)) / gcd(+d1||1, +d2||1);
  return (
    <CalcShell title="Mixed Numbers Calculator" subtitle="Add, subtract, multiply, and divide mixed numbers.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <input type="number" value={w1} onChange={e=>setW1(+e.target.value||0)} className="w-16" data-testid="mn-w1"/>
            <div className="flex flex-col"><input type="number" value={n1} onChange={e=>setN1(+e.target.value||0)} className="w-16" data-testid="mn-n1"/><div className="border-t border-slate-500 my-0.5"/><input type="number" value={d1} onChange={e=>setD1(+e.target.value||1)} className="w-16" data-testid="mn-d1"/></div>
            <select value={op} onChange={e=>setOp(e.target.value)} className="!w-14" data-testid="mn-op"><option value="+">+</option><option value="-">−</option><option value="*">×</option><option value="/">÷</option></select>
            <input type="number" value={w2} onChange={e=>setW2(+e.target.value||0)} className="w-16" data-testid="mn-w2"/>
            <div className="flex flex-col"><input type="number" value={n2} onChange={e=>setN2(+e.target.value||0)} className="w-16" data-testid="mn-n2"/><div className="border-t border-slate-500 my-0.5"/><input type="number" value={d2} onChange={e=>setD2(+e.target.value||1)} className="w-16" data-testid="mn-d2"/></div>
          </div>
        </div>
        <div>
          <div className="result-panel"><div className="result-title">Result</div>
            <div className="text-2xl font-bold text-blue-700" data-testid="mn-result">{sign}{whole > 0 ? whole : ''} {rem > 0 ? `${rem}/${sd}` : (whole > 0 ? '' : '0')}</div>
            <div className="text-slate-600 text-sm mt-1">Improper: {sign}{sn}/{sd} · Decimal: {(num/den).toFixed(6).replace(/\.?0+$/,'')}</div>
          </div>
        </div>
      </div>

      {/* Further Explanation Toggle */}
      <div className="mt-6">
        <button onClick={() => setShowExpl(v => !v)} data-testid="mn-further-toggle"
          className="text-sm px-4 py-1.5 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium">
          {showExpl ? '− Hide further explanation' : '+ Show further explanation'}
        </button>
        {showExpl && (
          <div className="mt-4 p-5 bg-blue-50 border border-blue-100 rounded-xl" data-testid="mn-further-panel">
            <h3 className="text-lg font-bold text-slate-900 mb-3">Further explanation</h3>
            <div className="space-y-3 text-slate-800 text-[15px] leading-7">
              <div>For the mixed number problem:
                <div className="mt-1 font-mono text-lg">
                  {w1} <sup>{n1}</sup>/<sub>{d1}</sub> {op === '+' ? '+' : op === '-' ? '−' : op === '*' ? '×' : '÷'} {w2} <sup>{n2}</sup>/<sub>{d2}</sub> = ?
                </div>
              </div>
              <div>
                <b>Step 1 — Convert each mixed number to an improper fraction</b> by multiplying the whole part by the denominator and adding the numerator:
                <div className="mt-1 font-mono">
                  {w1} <sup>{n1}</sup>/<sub>{d1}</sub> = ({w1}×{d1} + {n1}) / {d1} = <b>{A}/{d1}</b><br />
                  {w2} <sup>{n2}</sup>/<sub>{d2}</sub> = ({w2}×{d2} + {n2}) / {d2} = <b>{B}/{d2}</b>
                </div>
              </div>
              {(op === '+' || op === '-') && (
                <div>
                  <b>Step 2 — Find a common denominator.</b> LCM of {d1} and {d2} is <b>{lcm}</b>.
                  <div className="mt-1 font-mono">
                    {A}/{d1} = {A * (lcm / (+d1||1))}/{lcm}<br />
                    {B}/{d2} = {B * (lcm / (+d2||1))}/{lcm}
                  </div>
                  <div className="mt-1"><b>Step 3 — {op === '+' ? 'Add' : 'Subtract'} the numerators:</b></div>
                  <div className="font-mono">
                    {A * (lcm / (+d1||1))}/{lcm} {op === '+' ? '+' : '−'} {B * (lcm / (+d2||1))}/{lcm}
                    {' = '}{A * (lcm / (+d1||1)) + (op === '+' ? 1 : -1) * B * (lcm / (+d2||1))}/{lcm}
                  </div>
                </div>
              )}
              {op === '*' && (
                <div><b>Step 2 — Multiply numerators together and denominators together:</b>
                  <div className="font-mono mt-1">{A}/{d1} × {B}/{d2} = ({A}×{B}) / ({d1}×{d2}) = {A * B}/{(+d1||1) * (+d2||1)}</div>
                </div>
              )}
              {op === '/' && (
                <div><b>Step 2 — Divide by flipping the second fraction and multiplying:</b>
                  <div className="font-mono mt-1">{A}/{d1} ÷ {B}/{d2} = {A}/{d1} × {d2}/{B} = ({A}×{d2}) / ({d1}×{B}) = {A * (+d2||1)}/{(+d1||1) * B}</div>
                </div>
              )}
              <div>
                <b>Step 4 — Simplify</b> by dividing both by the GCD ({gcd(Math.abs(num), Math.abs(den))}): <span className="font-mono">{sign}{sn}/{sd}</span>
              </div>
              <div>
                <b>Step 5 — Convert back to a mixed number.</b> Divide numerator by denominator, take the quotient as the whole part and the remainder over the denominator:
                <div className="font-mono mt-1">
                  {sign}{sn}/{sd} = {sign}{whole}{rem > 0 ? <> <sup>{rem}</sup>/<sub>{sd}</sub></> : ''}
                </div>
              </div>
              {num < 0 && (
                <div className="italic text-slate-600">Note — the sign is negative because the subtraction resulted in a value less than zero. In a mixed number, the negative sign applies to the entire quantity.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </CalcShell>
  );
}

/* ---------------- Fraction — Big Number Fraction ---------------- */
export function BigFractionCalc() {
  const [n1, setN1] = useState('123456789');
  const [d1, setD1] = useState('987654321');
  const [op, setOp] = useState('+');
  const [n2, setN2] = useState('987654321');
  const [d2, setD2] = useState('123456789');
  const c = useMemo(() => {
    try {
      const N1 = BigInt(n1), D1 = BigInt(d1), N2 = BigInt(n2), D2 = BigInt(d2);
      let N = 0n, D = 1n;
      if (op === '+') { N = N1*D2 + N2*D1; D = D1*D2; }
      else if (op === '-') { N = N1*D2 - N2*D1; D = D1*D2; }
      else if (op === '*') { N = N1*N2; D = D1*D2; }
      else if (op === '/') { N = N1*D2; D = D1*N2; }
      const bgcd = (a, b) => { a = a < 0n ? -a : a; b = b < 0n ? -b : b; while (b) { [a, b] = [b, a % b]; } return a || 1n; };
      const g = bgcd(N, D);
      return { N: (N/g).toString(), D: (D/g).toString() };
    } catch { return { N: 'Invalid', D: '' }; }
  }, [n1, d1, n2, d2, op]);
  return (
    <CalcShell title="Big Number Fraction Calculator" subtitle="Arithmetic on fractions with arbitrarily-large numerators and denominators.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="flex flex-col flex-1"><input value={n1} onChange={e=>setN1(e.target.value)} className="font-mono" data-testid="bf-n1"/><div className="border-t border-slate-500 my-0.5"/><input value={d1} onChange={e=>setD1(e.target.value)} className="font-mono" data-testid="bf-d1"/></div>
            <select value={op} onChange={e=>setOp(e.target.value)} className="!w-16"><option value="+">+</option><option value="-">−</option><option value="*">×</option><option value="/">÷</option></select>
            <div className="flex flex-col flex-1"><input value={n2} onChange={e=>setN2(e.target.value)} className="font-mono" data-testid="bf-n2"/><div className="border-t border-slate-500 my-0.5"/><input value={d2} onChange={e=>setD2(e.target.value)} className="font-mono" data-testid="bf-d2"/></div>
          </div>
        </div>
        <div>
          <div className="result-panel"><div className="result-title">Result (simplified)</div>
            <div className="font-mono text-lg break-all mt-1" data-testid="bf-result">{c.N} / {c.D}</div>
          </div>
        </div>
      </div>
    </CalcShell>
  );
}
